import { useQueryClient, type QueryClient } from "@tanstack/react-query";

import { useAuth } from "../../context/auth-context";
import type {
  GiftCardCatalog,
  GiftCardDetailEnvelope,
  GiftCardsListEnvelope,
  GiftCardListStatus,
} from "./giftCardCatalog";
import { apiQueryKey } from "./queryKeys";
import { useApiQuery } from "./useApiQuery";

export function giftCardDetailQueryKey(cardId: string) {
  return apiQueryKey("gift-cards", "detail", cardId);
}

export function giftCardsListQueryKey(status: GiftCardListStatus) {
  return apiQueryKey("gift-cards", "list", status);
}

export function invalidateGiftCardCatalogQueries(client: QueryClient) {
  void client.invalidateQueries({ queryKey: apiQueryKey("gift-cards") });
}

export function useGiftCardDetailQuery(
  cardId: string,
  options?: { enabled?: boolean }
) {
  const { isAuthenticated } = useAuth();
  const { enabled: enabledOpt = true } = options ?? {};

  return useApiQuery<GiftCardDetailEnvelope, GiftCardCatalog | null>({
    queryKey: giftCardDetailQueryKey(cardId),
    url: `api/gift-cards/${cardId}`,
    enabled: Boolean(isAuthenticated && cardId && enabledOpt),
    select: (env) => env?.data?.giftCard ?? null,
  });
}

export function useGiftCardsListQuery(
  status: GiftCardListStatus,
  options?: { enabled?: boolean }
) {
  const { isAuthenticated } = useAuth();
  const { enabled: enabledOpt = true } = options ?? {};

  return useApiQuery<GiftCardsListEnvelope, GiftCardCatalog[]>({
    queryKey: giftCardsListQueryKey(status),
    url: "api/gift-cards",
    params: { status },
    enabled: Boolean(isAuthenticated && enabledOpt),
    select: (env) => env?.data?.giftCards ?? [],
  });
}

export function useInvalidateGiftCardCatalog() {
  const client = useQueryClient();
  return () => invalidateGiftCardCatalogQueries(client);
}
